import { Asset } from '../../interfaces';
import { Nullable } from '../../interfaces';

import { POLLING_INTERVAL_IN_MS } from '../../constants';
import { walletAddressAtom } from '../wallet';
import assetsAtom from './assetsAtom';
import nostraTokenBalancesAtom, { NostraTokenBalanceMap } from './nostraTokenBalanceAtom';
import { createAsyncAtom } from '../../lib/state';
import accountsAtom from './accountsAtom';

export interface AssetVariant {
  address: string;
  isLending: boolean;
  isCollateral: boolean;
}

export interface AssetVariantMap {
  [walletAddressAccountIdAssetAddress: string]: AssetVariant;
}

const fetchData = (
  walletAddress: string,
  activeAccountId: number,
  asset: Asset,
  nostraTokenBalanceMap: NostraTokenBalanceMap,
): Nullable<AssetVariant> => {
  const nostraInterestBearingDataKey = `${walletAddress}-${activeAccountId}-${asset.nostraInterestBearingTokenAddress}`;
  const nostraInterestBearingCollateralDataKey = `${walletAddress}-${activeAccountId}-${asset.nostraInterestBearingCollateralTokenAddress}`;
  const nostraDataKey = `${walletAddress}-${activeAccountId}-${asset.nostraTokenAddress}`;
  const nostraCollateralDataKey = `${walletAddress}-${activeAccountId}-${asset.nostraCollateralTokenAddress}`;

  const nostraInterestBearingBalance = nostraTokenBalanceMap[nostraInterestBearingDataKey];
  const nostraInterestBearingCollateralBalance = nostraTokenBalanceMap[nostraInterestBearingCollateralDataKey];
  const nostraBalance = nostraTokenBalanceMap[nostraDataKey];
  const nostraCollateralBalance = nostraTokenBalanceMap[nostraCollateralDataKey];

  if (
    !nostraInterestBearingBalance ||
    !nostraInterestBearingCollateralBalance ||
    !nostraBalance ||
    !nostraCollateralBalance
  ) {
    return null;
  }

  const balancesData = [
    {
      isLending: true,
      isCollateral: true,
      balance: nostraInterestBearingCollateralBalance,
    },
    {
      isLending: true,
      isCollateral: false,
      balance: nostraInterestBearingBalance,
    },
    {
      isLending: false,
      isCollateral: false,
      balance: nostraBalance,
    },
    {
      isLending: false,
      isCollateral: true,
      balance: nostraCollateralBalance,
    },
  ];

  const sortedBalancesData = balancesData.sort((a, b) => {
    if (a.balance.gt(b.balance)) {
      return -1;
    }
    if (a.balance.lt(b.balance)) {
      return 1;
    }
    return 0;
  });

  return {
    address: asset.address,
    isLending: sortedBalancesData[0].isLending,
    isCollateral: sortedBalancesData[0].isCollateral,
  };
};

const assetVariantAtom = createAsyncAtom(async get => {
  const walletAddress = await get(walletAddressAtom);
  const accounts = await get(accountsAtom);
  const nostraTokenBalances = await get(nostraTokenBalancesAtom);
  const assets = await get(assetsAtom);

  if (!walletAddress || !assets) {
    return null;
  }

  const assetVariantMap: AssetVariantMap = {};

  const activeAccountIds = accounts
    .map((account, accountId) => [account, accountId] as const)
    .filter(([account]) => account !== '')
    .map(([, accountId]) => accountId);

  activeAccountIds.forEach(accountId => {
    assets.forEach(asset => {
      const result = fetchData(walletAddress, accountId, asset, nostraTokenBalances);

      if (result) {
        assetVariantMap[`${walletAddress}-${accountId}-${asset.address}`] = result;
      }
    });
  });

  return assetVariantMap;
}, POLLING_INTERVAL_IN_MS);

export default assetVariantAtom;
